import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import {
    clearData,
    getSeverTime,
    resetSeverTime,
    setSeverTime,
} from "@rogue_5/service"
import SKLand from "@skland/one-bridge"
import { useEventListener } from "ahooks"
import classNames from "classnames"
import { clamp, round } from "lodash-es"
import { SafeAreaView, useLandscapeDisplayInfo } from "@hg/layouts"
import { useShowRoleSelect } from "@hg/one-account"

import Logo from "@/components/Logo"
import { useScale } from "@/hooks"
import { useETL, useTranslation } from "@/locales"
import {
    getRogue5SpecialUrl,
    getRogueUrl,
    isMobile,
    supportsHEVCAlpha,
} from "@/utils/misc"
import { webLogin } from "@/utils/sdk"
import Menu from "../../components/Menu"
import VideoPlayer from "../../components/VideoPlayer"
import { EPage, ESubPage, GlobalContext, IPageProps } from "../../context"
import { backgroundVideo, buildings, useWebM } from "./building"

import bg from "../../assets/img/lobby/bg.jpg"
import ticketPng from "../../assets/img/lobby/ticket/ticket.png"
import ticketWebP from "../../assets/img/lobby/ticket/ticket.webp"
import styles from "./index.module.less"

const PageLobby: React.FC<IPageProps> = (props) => {
    const { className, style } = props

    const {
        state: {
            fontSize,
            user,
            hasRole,
            currentPage,
            currentSubPage,
            showRegistry,
        },
        dispatch,
    } = useContext(GlobalContext)
    const scale = useScale(fontSize)
    const { t } = useTranslation()
    const { inRotate, width, height } = useLandscapeDisplayInfo()

    const [ticketActive, setTicketActive] = useState(false)

    const showRoleSelect = useShowRoleSelect()
    const collect = useETL()
    const handleClickEntry = useCallback(
        async (page: ESubPage) => {
            // collect("button_click", { button_name: `lobby_${page}` })
            if (!__ROGUE_5_PRE) {
                if (page === ESubPage.SP) {
                    window.open(getRogue5SpecialUrl())
                    return
                }
            }

            // 数据模块和游戏模块需要登陆
            if ([ESubPage.Record, ESubPage.Game].includes(page) && !user) {
                const loginResult = await webLogin(dispatch)
                if (!loginResult) {
                    return
                }
                dispatch({
                    type: "updateContext",
                    payload: { currentSubPage: page },
                })
                return
            }

            dispatch({
                type: "updateContext",
                payload: { currentSubPage: page },
            })
        },
        [collect, dispatch, hasRole, showRoleSelect, user]
    )

    const scene = useRef<HTMLDivElement>(null)
    const animationId = useRef(0)
    const fromPoint = useRef({ x: 0, y: 0 })
    const toPoint = useRef({ x: 0, y: 0 })
    const currentPoint = useRef({ x: 0, y: 0 })
    const lastTouchPoint = useRef({ x: 0, y: 0 })

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (currentPage !== EPage.Lobby || !scene.current || isMobile()) {
                return
            }

            const { clientX, clientY } = e
            const x = inRotate ? clientY : clientX
            const y = inRotate ? height - clientX : clientY

            const { clientWidth, clientHeight } = scene.current

            fromPoint.current = currentPoint.current
            toPoint.current = {
                x:
                    (x - width / 2) *
                    (((clientWidth * scale - width) * 0.5) / (width * 0.5)) *
                    -1,
                y:
                    (y - height / 2) *
                    (((clientHeight * scale - height) * 0.5) / (height * 0.5)) *
                    -1,
            }
        },
        [currentPage, height, inRotate, scale, width]
    )

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            if (currentPage !== EPage.Lobby) {
                return
            }

            const { clientX, clientY } = e.touches[0]
            if (inRotate) {
                lastTouchPoint.current = { x: clientY, y: height - clientX }
            } else {
                lastTouchPoint.current = { x: clientX, y: clientY }
            }
        },
        [currentPage, height, inRotate]
    )

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (currentPage !== EPage.Lobby || !scene.current) {
                return
            }

            const { clientWidth, clientHeight } = scene.current

            const paddingX = (clientWidth * scale - width) * 0.5
            const paddingY = (clientHeight * scale - height) * 0.5

            let { clientX, clientY } = e.touches[0]
            if (inRotate) {
                clientX = e.touches[0].clientY
                clientY = height - e.touches[0].clientX
            }

            const dx = clientX - lastTouchPoint.current.x
            const dy = clientY - lastTouchPoint.current.y
            lastTouchPoint.current = { x: clientX, y: clientY }

            fromPoint.current = currentPoint.current
            toPoint.current = {
                x: clamp(
                    toPoint.current.x + dx * (paddingX / (width * 0.5)),
                    -paddingX,
                    paddingX
                ),
                y: clamp(
                    toPoint.current.y + dy * (paddingY / (height * 0.5)),
                    -paddingY,
                    paddingY
                ),
            }
        },
        [currentPage, height, inRotate, scale, width]
    )

    useEventListener("mousemove", handleMouseMove, { target: window })
    useEventListener("touchstart", handleTouchStart, { target: window })
    useEventListener("touchmove", handleTouchMove, { target: window })

    useEventListener(
        "resize",
        () => {
            if (scene.current) {
                fromPoint.current = { x: 0, y: 0 }
                toPoint.current = { x: 0, y: 0 }
                currentPoint.current = { x: 0, y: 0 }
                lastTouchPoint.current = { x: 0, y: 0 }
                scene.current.style.transform = `scale(${scale})`
            }
        },
        { target: window }
    )

    const moveStep = useCallback(() => {
        if (!currentSubPage && !showRegistry) {
            const point = (currentPoint.current = {
                x:
                    currentPoint.current.x +
                    (toPoint.current.x - currentPoint.current.x) * 0.1,
                y:
                    currentPoint.current.y +
                    (toPoint.current.y - currentPoint.current.y) * 0.1,
            })

            if (scene.current) {
                const x = round(point.x / scale, 2)
                const y = round(point.y / scale, 2)
                const transform = `scale(${scale}) translate3d(${x}px, ${y}px, 0)`

                if (scene.current.style.transform !== transform) {
                    scene.current.style.transform = transform
                }
            }
        }

        animationId.current = window.requestAnimationFrame(() => {
            moveStep()
        })
    }, [currentSubPage, scale, showRegistry])

    useEffect(() => {
        if (currentPage !== EPage.Loading) {
            animationId.current = window.requestAnimationFrame(() => {
                moveStep()
            })
        } else {
            window.cancelAnimationFrame(animationId.current)
        }

        return () => {
            window.cancelAnimationFrame(animationId.current)
        }
    }, [currentPage, moveStep])

    const [uid, setUid] = useState("")
    const [time, setTime] = useState("")
    const clear = async () => {
        await clearData(uid)
        alert("用户数据已清除,请刷新页面！")
        setUid("")
    }
    const set = async () => {
        await setSeverTime(time)
        alert("服务器时间已设置")
        setTime("")
    }
    const getTime = async () => {
        const res = await getSeverTime()
        alert(res?.data.time)
    }
    const resetTime = async () => {
        await resetSeverTime()
        alert("服务器时间已重置")
    }

    return (
        <div className={classNames(styles.page, className)} style={style}>
            <div
                ref={scene}
                className={styles.buildings}
                style={{ transform: `scale(${scale})` }}
            >
                <VideoPlayer
                    sources={[
                        {
                            src: backgroundVideo.mov,
                            type: "video/quicktime",
                        },
                        {
                            src: backgroundVideo.webm,
                            type: 'video/webm; codecs="vp9, vorbis"',
                        },
                        {
                            src: backgroundVideo.mp4,
                            type: "video/mp4",
                        },
                    ]}
                    poster={bg}
                />
                {buildings.map((building) => (
                    <div
                        key={building.id}
                        className={classNames(
                            styles.building,
                            building.active && styles.active
                        )}
                        data-id={building.id}
                        onClick={() => {
                            if (building.active) {
                                handleClickEntry(building.type)
                            }
                        }}
                    >
                        <div className={styles.bg} />
                        {!isMobile() && (
                            <div className={styles.content}>
                                {useWebM || supportsHEVCAlpha() ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        preload="auto"
                                    >
                                        <source
                                            src={building.mov}
                                            type="video/quicktime"
                                        />
                                        <source
                                            src={building.webm}
                                            type="video/webm"
                                        />
                                    </video>
                                ) : (
                                    <div
                                        className={styles.img}
                                        style={{
                                            backgroundImage: `url(${building.png})`,
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        {building.active ? (
                            <div className={styles.title}>
                                <img
                                    src={building.titleImg}
                                    alt={t(building.title)}
                                    className={styles.img}
                                />
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
            <SafeAreaView
                mode="margin"
                edges={["left"]}
                className={classNames(
                    styles.ticket,
                    ticketActive && styles.active
                )}
                onClick={() => setTicketActive(!ticketActive)}
            >
                <div className={styles.arrow} />
                <div className={styles.inner}>
                    <picture>
                        <source srcSet={ticketWebP} type="image/webp" />
                        <img src={ticketPng} alt="ticket" />
                    </picture>
                    <div className={styles.links}>
                        <a
                            className={styles.item}
                            href="https://arknights.jp/crimsonsolitaire"
                            target="_blank"
                            rel="noreferrer"
                        />
                        <a
                            className={styles.item}
                            href="https://arknights.jp/caerulaarbor"
                            target="_blank"
                            rel="noreferrer"
                        />
                        <a
                            className={styles.item}
                            href="https://arknights.jp/samiexpedition"
                            target="_blank"
                            rel="noreferrer"
                        />
                        <a
                            className={styles.item}
                            href="https://arknights.jp/furnacesidefables"
                            target="_blank"
                            rel="noreferrer"
                        />
                    </div>
                </div>
            </SafeAreaView>
            <Logo
                safeArea={false}
                className={styles.logo}
                returnClassName={styles.return}
            />
            <Menu />
            {/*<div className={styles.qa}>*/}
            {/*    <div>*/}
            {/*        <input*/}
            {/*            type="text"*/}
            {/*            placeholder="输入UID"*/}
            {/*            value={uid}*/}
            {/*            onChange={(e) => setUid(e.target.value)}*/}
            {/*        />*/}
            {/*        <button onClick={clear}>清除用户数据</button>*/}
            {/*    </div>*/}
            {/*    <div>*/}
            {/*        <input*/}
            {/*            type="text"*/}
            {/*            placeholder="输入时间"*/}
            {/*            value={time}*/}
            {/*            onChange={(e) => setTime(e.target.value)}*/}
            {/*        />*/}
            {/*        <button onClick={set}>设置服务器时间</button>*/}
            {/*    </div>*/}
            {/*    <div>*/}
            {/*        <button onClick={resetTime}>重置服务器时间</button>*/}
            {/*        <button onClick={getTime}>获取服务器时间</button>*/}
            {/*    </div>*/}
            {/*</div>*/}
        </div>
    )
}

export default PageLobby
